import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { calculateDiscountedPrice } from '../utils/discountUtils';
import { useLanguage } from './LanguageContext';
import keywordsData from '../lang/keywords.json';
import { formatPrice } from '../components/CarrancyTrans';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [pindingItems, setPindingItems] = useState([]);
  const [cartCurrency, setCartCurrency] = useState('MYR');
  const [deliverFees, setDeliverFees] = useState(0);
  const [serviceChargePercent, setServiceChargePercent] = useState(0);
  const [serviceChargeValue, setServiceChargeValue] = useState(0);
  const [thresholdDiscount, setThresholdDiscount] = useState({ applicable: false, amount: 0, percent: 0, min_amount: 0 , thresholdvalue: 0 });
  const [subtotalBeforeThreshold, setSubtotalBeforeThreshold] = useState(0);
  const [subtotalAfterThreshold, setSubtotalAfterThreshold] = useState(0);
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tableScan, setTableScan] = useState(Number(localStorage.getItem('tableId')) || 0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [openLimitModel, setOpenLimitModel] = useState(false);

    // VIP Table States
  const [isVipTable, setIsVipTable] = useState(false);
  const [minCharge, setMinCharge] = useState(0);
  const [currentOrder, setCurrentOrder] = useState(0);
  const [tableInfo, setTableInfo] = useState(null);
  const [minChargeError, setMinChargeError] = useState(false);
  const [minChargeMessage, setMinChargeMessage] = useState('');
  const [isOrder, setIsOrder] = useState(false);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

const resetTable = () => {
  setTableScan(0);
  clearVipTableInfo(); // Clear VIP info
  localStorage.removeItem('tableId');
  localStorage.removeItem('table_persons');
  localStorage.removeItem('vip_table_info');
  localStorage.removeItem('current_rest_id');
  setCart([]);
  setDeliverFees(0);
  setServiceChargeValue(0);
  setThresholdDiscount({ applicable: false, amount: 0, percent: 0, min_amount: 0, thresholdvalue: 0 });
  setSubtotalBeforeThreshold(0);
  setSubtotalAfterThreshold(0);
};


  // Set VIP table info
  const setVipTableInfo = (info) => {
    setTableInfo(info);
    setIsVipTable(info?.is_vip || false);
    setMinCharge(info?.min_charge || 0);
    setMinChargeError(false);
    setMinChargeMessage('');
    
    // Store in localStorage for persistence
    if (info) {
      localStorage.setItem('vip_table_info', JSON.stringify(info));
    } else {
      localStorage.removeItem('vip_table_info');
    }
  };

  // Load VIP info from localStorage on mount
  useEffect(() => {
    const savedVipInfo = localStorage.getItem('vip_table_info');
    if (savedVipInfo) {
      try {
        const info = JSON.parse(savedVipInfo);
        setIsVipTable(info.is_vip || false);
        setMinCharge(info.min_charge || 0);
        setTableInfo(info);
      } catch (e) {
        console.error('Error parsing VIP info:', e);
      }
    }
  }, []);

  // Clear VIP table info
  const clearVipTableInfo = () => {
    setIsVipTable(false);
    setMinCharge(0);
    setTableInfo(null);
    setIsOrder(false);
    setMinChargeError(false);
    setMinChargeMessage('');
    localStorage.removeItem('vip_table_info');
  };

  // Check if cart meets minimum charge requirement
  const checkMinimumCharge = (cartTotal) => {
    if (isVipTable && minCharge > 0) {
      if (cartTotal < minCharge) {
        setMinChargeError(true);
        setMinChargeMessage(
          language === 'ar'
            ? `الحد الأدنى للطلب لهذه الطاولة هو ${formatPrice(minCharge,cartCurrency,language)}. الرجاء إضافة منتجات بقيمة ${formatPrice((minCharge - cartTotal).toFixed(2),cartCurrency,language)}`
            : `Minimum order amount for this table is ${formatPrice(minCharge,cartCurrency,language)}. Please add products worth ${formatPrice((minCharge - cartTotal).toFixed(2),cartCurrency,language)}`
        );
        return false;
      } else {
        setMinChargeError(false);
        setMinChargeMessage('');
        return true;
      }
    }
    return true;
  };

  // Get minimum charge remaining
  const getRemainingMinCharge = () => {
    if (isVipTable && !isOrder && minCharge > 0) {
      const currentTotal = cartTotal;
      if (currentTotal < minCharge) {
        return minCharge - currentTotal;
      }
    }
    return 0;
  };

  // Get minimum charge progress percentage
  const getMinChargeProgress = () => {
    if (isVipTable && !isOrder && minCharge > 0) {
      const progress = (cartTotal / minCharge) * 100;
      return Math.min(progress, 100);
    } else if (isVipTable && isOrder  && minCharge > 0){
      return Math.min(100, 100);
    }
    return 0;
  };

  const clearCart = async () => {
    try {
      if (user) {
        await api.delete('/cart/clear');
      }
      setCart([]);
      setDeliverFees(0);
      setServiceChargeValue(0);
      setThresholdDiscount({ applicable: false, amount: 0, percent: 0, min_amount: 0, thresholdvalue: 0 });
      setSubtotalBeforeThreshold(0);
      setSubtotalAfterThreshold(0);
      if(!tableScan){
        localStorage.removeItem('tableId');
        localStorage.removeItem('current_rest_id');
        setTableScan(0);
      }
      setSnackbar({
        open: true,
        message: language === 'ar' ? keywordsData.delete_cart_txt_ar : keywordsData.delete_cart_txt_en,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error clearing cart:', err);
    }
  };

  const fetchCart = async () => {
    if (!tableScan) {
      if (!user) return;
    }
    const tablePersons = localStorage.getItem('table_persons');
    const userLocationData = localStorage.getItem('user_location');

    try {
      setLoading(true);
      const restaurantId = localStorage.getItem('current_rest_id') ;
      if(!restaurantId) return;

    // ✅ Parse the stored JSON string back into an object
    let userLat = 0;
    let userLong = 0;

    if (userLocationData) {
      try {
        const parsed = JSON.parse(userLocationData);
        userLat = parsed?.lat ?? 0;
        userLong = parsed?.lng ?? 0;
      } catch (err) {
        console.warn('Invalid user_location in localStorage:', err);
      }
    }
      const response = await api.get('/cart', {
        params: { tableScan: tableScan,restID:restaurantId, pax_no: tablePersons || 1 ,userLat, userLong}
      });
      
      const data = response.data;
      // The response.data is directly the array of cart items
    let cartItems = [];
    
    if (Array.isArray(response.data)) {
      cartItems = response.data;
    } else if (response.data?.items && Array.isArray(response.data.items)) {
      cartItems = response.data.items;
    } else if (response.data?.cart_items && Array.isArray(response.data.cart_items)) {
      cartItems = response.data.cart_items;
    } else {
      cartItems = [];
    }
      if (response.data?.pending_order_items) {
        setPindingItems(response.data.pending_order_items);
      }
      setCart(cartItems);
      setDeliverFees(data?.delivery_value || data?.summary?.delivery_fee || 0);
      setCartCurrency(data?.currency);
      // Safely set service charge values
      if (data?.service_charge) {
        setServiceChargeValue(data.service_charge.amount || 0);
        setServiceChargePercent(data.service_charge.percent || 0);
      }
      
      // Set threshold discount data
      if (data?.threshold_discount) {
        setThresholdDiscount({
          applicable: data.threshold_discount.applicable || false,
          amount: data.threshold_discount.amount || 0,
          percent: data.threshold_discount.percent || 0,
          min_amount: data.threshold_discount.min_amount || 0,
          thresholdvalue: data.threshold_discount.thresholdvalue || 0
        });
      }
      setIsOrder(response.data.table_order_exists);
      setCurrentOrder(response.data?.pending_order_items[0]?.order_id || 0);
      setSubtotalBeforeThreshold(data?.subtotal_before_threshold || 0);
      setSubtotalAfterThreshold(data?.subtotal_after_threshold || 0);
      
    } catch (err) {
      console.error('Error fetching cart:', err);
    } finally {
      setLoading(false);
    }
  };

// In CartContext.js - Alternative approach (accepts object)
// In CartContext.js - Updated to handle both signatures
const addToCart = async (productOrObject, quantity = 1, selectedSize = null, selectedExtras = [],limit = 0,category_limit = 0) => {
  
  let productId;
  let finalQuantity;
  let finalSelectedSize;
  let finalSelectedExtras;
  let promotionData = null;
    // Helper function for product limit check
  const checkProductLimit = async (productId, quantityToAdd, productLimit) => {
    // Check if we're in table scan mode (tableScan > 0)
    const isTableMode = tableScan > 0;
    
    console.log('=== PRODUCT LIMIT CHECK DEBUG ===');
    console.log('tableScan value:', tableScan);
    console.log('isTableMode:', isTableMode);
    console.log('productLimit:', productLimit);
    console.log('quantityToAdd:', quantityToAdd);
    
    // Only check if ALL conditions are met:
    // 1. We're in table mode (tableId exists)
    // 2. Product has a limit (limit > 0)
    if (!isTableMode) {
      console.log('Not in table mode, skipping product limit check');
      //return true;
    }
    
    if (productLimit <= 0) {
      console.log('No product limit set, skipping');
      return true;
    }
        
    try {
      let pendingOrderItems = [];
      if (pindingItems) {
        pendingOrderItems = pindingItems;
      }
      
      console.log('Cart items found:', cart.length);
      
      let currentTotalQuantity = 0;
      cart.forEach(item => {
        const itemProductId = item.product_id || item.product?.id;
        if (itemProductId === productId) {
          currentTotalQuantity += item.quantity;
          console.log(`Found item: product_id=${itemProductId}, quantity=${item.quantity}`);
        }
      });
      
      let pendingOrderQuantity = 0;
      pendingOrderItems.forEach(item => {
        if (item.product_id === productId) {
          pendingOrderQuantity += item.quantity;
        }
      });
      
      const totalExistingQuantity = currentTotalQuantity + pendingOrderQuantity;
      const newTotal = totalExistingQuantity + quantityToAdd;
      
      console.log(`Current cart quantity for product ${productId}: ${currentTotalQuantity}`);
      console.log(`Pending order quantity: ${pendingOrderQuantity}`);
      console.log(`Total existing quantity: ${totalExistingQuantity}`);
      console.log(`New total would be: ${newTotal}`);
      console.log(`Product limit is: ${productLimit}`);
      
      if (newTotal > productLimit) {
        console.log('PRODUCT LIMIT WILL BE EXCEEDED! Blocking addition.');

        setOpenLimitModel(true);
        return false;
      }
      
      console.log('Product limit check passed');
      return true;
    } catch (err) {
      console.error('Error fetching cart for product limit check:', err);
      return true;
    }
  };

  // Check if first parameter is an object (has product_id property)
  if (typeof productOrObject === 'object' && productOrObject !== null && productOrObject.product_id) {
    // It's the customized item object from ProductOptionsDialog
    console.log('Adding customized item with object:', productOrObject);
    
    productId = productOrObject.product_id;
    finalQuantity = productOrObject.quantity || 1;
    finalSelectedSize = productOrObject.selected_size || null;
    finalSelectedExtras = productOrObject.selected_extras || [];
        // Check PRODUCT limit SECOND
    const productLimitCheckPassed = await checkProductLimit(productId, finalQuantity, limit);
    if (!productLimitCheckPassed) {
      return; // Stop execution
    }
    
    // Extract promotion data if it exists
    if (productOrObject.applied_promotion_id) {
      promotionData = {
        applied_promotion_id: productOrObject.applied_promotion_id,
        applied_promotion_data: productOrObject.applied_promotion_data,
        free_reward_item_id: productOrObject.free_reward_item_id,
        free_reward_item_type: productOrObject.free_reward_item_type,
        promotion_qualifying_amount: productOrObject.promotion_qualifying_amount
      };
    }
  } else {
    // It's a simple product ID (from the simple add to cart button)
    console.log('Adding simple product with ID:', productOrObject);
    
    productId = productOrObject;
    finalQuantity = quantity;
    finalSelectedSize = selectedSize;
    finalSelectedExtras = selectedExtras;
    const productLimitCheckPassed = await checkProductLimit(productId, finalQuantity, limit);
    if (!productLimitCheckPassed) {
      return; // Stop execution
    }
  }

  if (!tableScan) {
    if (!user) {
      window.location.href = '../login';
      return;
    }
  }
  setIsAddingToCart(true);

  try {
    // Calculate final price
    let finalPrice = 0;
    let basePrice = 0;
    
    const productResponse = await api.get(`/products/${productId}`);
    const product = productResponse.data;
    
    basePrice = product.final_price || product.price || 0;
    finalPrice = basePrice;

    // Add size price
    if (finalSelectedSize) {
      finalPrice += finalSelectedSize.price || 0;
    }

    // Add extras prices
    if (finalSelectedExtras.length > 0) {
      finalPrice += finalSelectedExtras.reduce((sum, extra) => sum + (extra.price || 0), 0);
    }

    // Prepare cart data
    const cartData = {
      product_id: productId,
      quantity: finalQuantity,
      language,
      tableScan,
      selected_size: finalSelectedSize,
      selected_extras: finalSelectedExtras,
      base_price: basePrice,
      final_unit_price: finalPrice
    };

    // Add promotion data if available
    if (promotionData) {
      if (promotionData.applied_promotion_id) {
        cartData.applied_promotion_id = promotionData.applied_promotion_id;
      }
      if (promotionData.applied_promotion_data) {
        cartData.applied_promotion_data = promotionData.applied_promotion_data;
      }
      if (promotionData.free_reward_item_id) {
        cartData.free_reward_item_id = promotionData.free_reward_item_id;
      }
      if (promotionData.free_reward_item_type) {
        cartData.free_reward_item_type = promotionData.free_reward_item_type;
      }
      if (promotionData.promotion_qualifying_amount) {
        cartData.promotion_qualifying_amount = promotionData.promotion_qualifying_amount;
      }
    }

    console.log('Sending cart data:', cartData);

    await api.post('/cart', cartData);
    await fetchCart();
    
    setSnackbar({
      open: true,
      message: language === 'ar' ? keywordsData.add_cart_txt_ar : keywordsData.add_cart_txt_en,
      severity: 'success'
    });
  } catch (err) {
    console.error('Error adding to cart:', err);
    setSnackbar({
      open: true,
      message: language === 'ar' ? keywordsData.add_fall_cart_txt_ar : keywordsData.add_fall_cart_txt_en,
      severity: 'error'
    });
  } finally {
    setIsAddingToCart(false);
  }
};

const addToCart1 = async (productId, quantity = 1) => {
    if (!tableScan) {
      if (!user) {
        window.location.href = '../login';
        return;
      }
    }

    try {
      await api.post('/cart', { product_id: productId, quantity, language, tableScan });
      await fetchCart();
      setSnackbar({
        open: true,
        message: language === 'ar' ? keywordsData.add_cart_txt_ar : keywordsData.add_cart_txt_en,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error adding to cart:', err);
      setSnackbar({
        open: true,
        message: language === 'ar' ? keywordsData.add_fall_cart_txt_ar : keywordsData.add_fall_cart_txt_en,
        severity: 'error'
      });
    }
  };

const updateQuantity = async (cartItemId, quantity, productLimit, categoryLimit) => {
  const isTableMode = tableScan > 0;
  
  // Find the cart item to get product and category info
  let cartItem = null;
  let productId = null;
  let categoryId = null;
  let cartItems = [];
  let pendingOrderItems = [];
  
  try {
    // Get pending order items
    if (pindingItems) {
      pendingOrderItems = pindingItems;
    }
    
    // Find the specific cart item
    cartItem = cart.find(item => item.id === parseInt(cartItemId));
    
    
    // Calculate pending order quantity for this specific product
    let pendingOrderProductQuantity = 0;
    
    pendingOrderItems.forEach(item => {
      if (item.product_id === productId) {
        pendingOrderProductQuantity += item.quantity;
      }
    });
        
    // Calculate totals including the new quantity
    const totalProductQuantity = quantity + pendingOrderProductQuantity;
    
    
    
    // Check PRODUCT limit SECOND
    if (productLimit > 0 && totalProductQuantity > productLimit) {
      setOpenLimitModel(true);
      return;
    }
    
    // Update quantity
    await api.put(`/cart/${cartItemId}`, { quantity });
    await fetchCart();
    
  } catch (err) {
    console.error('Error updating quantity:', err);
  }
};


  const removeFromCart = async (cartItemId) => {
    try {
      await api.delete(`/cart/delete/${cartItemId}`);
      await fetchCart();
      setSnackbar({
        open: true,
        message: language === 'ar' ? keywordsData.delete_product_cart_txt_ar : keywordsData.delete_product_cart_txt_en,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error removing from cart:', err);
    }
  };

  // Calculate product total from cart items
  const productTotal = cart.reduce(
    (total, item) => total + (item.final_price || item.product?.price || 0) * item.quantity,
    0
  );

  // Calculate total savings from product discounts
  const productSavings = cart.reduce(
    (savings, item) => savings + (item.discount_applied || 0) * item.quantity,
    0
  );

  // FIXED: Use subtotalAfterThreshold from backend if available, otherwise calculate
  const effectiveSubtotal = subtotalAfterThreshold > 0 ? subtotalAfterThreshold : productTotal;

  // Calculate cart total with all fees
  const cartTotal = effectiveSubtotal + deliverFees + serviceChargeValue;
   
  // Calculate total savings including threshold discount
  const totalSavings = productSavings + (thresholdDiscount.amount || 0);

  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  // Get individual item price (with discount if applicable)
  const getItemPrice = (item) => {
    return item.final_price || item.product?.price || 0;
  };

  // Get original price before discount
  const getOriginalPrice = (item) => {
    return item.product?.price || 0;
  };

  // Check if item has discount
  const hasDiscount = (item) => {
    return item.discount_applied > 0 || (item.product?.discount_percent > 0);
  };
  // Check minimum charge whenever cart total changes
  useEffect(() => {
    if (cartTotal > 0 || isVipTable) {
      checkMinimumCharge(cartTotal);
    }
  }, [cartTotal, isVipTable, minCharge]);

  useEffect(() => {
    if (!tableScan) {
      if (!user) {
        setCart([]);
      } else {
        fetchCart();
      }
    } else {
      fetchCart();
    }
  }, [user, tableScan]);

  const updateItemNotes = async (itemId, notes) => {
    setCart(prevCart => 
      prevCart.map(item => 
        item.id === itemId 
          ? { ...item, notes: notes }
          : item
      )
    );
  };

const updateTableScan = (newTableId) => {
  // If scanning a different table, clear VIP info
  if (newTableId !== tableScan) {
    clearVipTableInfo();
    localStorage.removeItem('vip_table_info');
  }
  setTableScan(newTableId);
};
  return (
    <CartContext.Provider
      value={{
        cart,
        cartOpen,
        setCartOpen,
        addToCart,
        updateQuantity,
        removeFromCart,
        cartTotal,
        productTotal,
        totalSavings,
        productSavings,
        deliverFees,
        serviceChargePercent,
        serviceChargeValue,
        thresholdDiscount,
        subtotalBeforeThreshold,
        subtotalAfterThreshold,
        effectiveSubtotal,
        cartCount,
        loading,
        clearCart,
        getItemPrice,
        getOriginalPrice,
        hasDiscount,
        handleCloseSnackbar,
        snackbar,
        setTableScan,
        fetchCart,
        cartCurrency,
        isAddingToCart,
        // VIP Table Exports
        isVipTable,
        minCharge,
        tableInfo,
        setVipTableInfo,
        clearVipTableInfo,
        checkMinimumCharge,
        getRemainingMinCharge,
        getMinChargeProgress,
        resetTable,
        updateTableScan,
        minChargeError,
        minChargeMessage,
        isOrder,
        openLimitModel,setOpenLimitModel,currentOrder

      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);